import {isEmpty, isEqual} from '@shared/helpers/lang'

import {getCardPromotionGroups} from '@web/actions/cardPromotion'
import {
    APIStatus,
    BannerTypeType,
    EasyCardItemType,
    PaymentsCardCodeType,
    PayMethod,
    PromotionCardCodeType,
    PromotionCardProductNameCodeType,
    PromotionStatus,
    PromotionType,
    PromotionUnApplicableReason,
} from '@web/core/constants'
import {getCardCodeName} from '@web/helpers/policies'
import {
    CardState,
    getCardEasyPaysFirstItem,
    getCommonInstallmentMinAmount,
    getCommonInstallments,
    setupLongInterestFreeInstallments,
    convertOverseaEasyCard,
    getInitialPromotionInfo,
    GroupItem,
    isCardProductPromotion,
    isApplicableCardProductCodes,
} from '@web/slices/card/slice'
import makeSlice from '@web/slices/card/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'
import {CardEasyPay, OverseaCardEasyPay} from '../../apis/interface/response/interface'
import {getPointPromotionStatusByApiResult} from '../../utils'

const convertEasyCard = (cardEasyPays: CardEasyPay[]): CardState['cardEasyPay']['items'] => {
    return cardEasyPays.map((cardEasyPay) => {
        const companyCode = cardEasyPay.cardCompanyCode as PaymentsCardCodeType
        return {
            category: cardEasyPay.cardCategory,
            companyCode,
            companyName: `${getCardCodeName(companyCode)}카드`,
            companyId: cardEasyPay.cardCompanyId,
            id: cardEasyPay.cardId,
            imageUrl: cardEasyPay.cardImageUrl,
            name: cardEasyPay.cardName,
            ownerType: cardEasyPay.cardOwnerType,
            productCode: cardEasyPay.cardProductCode,
            type: cardEasyPay.cardType ?? 'CREDIT',
            maskedNumber: cardEasyPay.maskedCardNumber,
            payService: {
                payable: cardEasyPay.payServiceAvailable,
                maintenance: cardEasyPay.payServiceMaintenanceSchedule,
                supportedMerchant: true,
            },
            longTermInstallment: cardEasyPay.longTermInstallment,
            registeredDateTime: cardEasyPay.registeredDateTime,
            reward: !cardEasyPay.rewardCard
                ? null
                : {
                      rewardAccumulateRate: cardEasyPay.rewardAccumulateRate,
                      rewardPointResidualAmount: cardEasyPay.rewardPointResidualAmount,
                      minAmount: 0,
                  },
            interestFreeBenefitPromotion: {
                isAvailable: true,
                minAmount: 0,
                month: 0,
            },
            plcc: cardEasyPay.hyundaiPlccCard
                ? {
                      type: 'NAVER_HYUNDAI',
                  }
                : null,
            issuerCode: cardEasyPay.cardIssuerCode,
            baseInstallmentInfo: getCommonInstallments(cardEasyPay.cardCompanyCode),
        }
    })
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
                const {payMethod, banner, pointPromotion} = payload

                state.cardEasyPay.bannerItemTypes =
                    banner.bannerListsByExposureAreaType['EASY_PAY_CREDIT_CARD']?.reduce<
                        {
                            cardCompanyCode: PromotionCardCodeType
                            bannerType: BannerTypeType
                            cardProductNameCode: Maybe<PromotionCardProductNameCodeType>
                        }[]
                    >((acc, {cardCompanyCode, bannerType, cardProductNameCode}) => {
                        if (cardCompanyCode) {
                            acc.push({
                                cardCompanyCode,
                                bannerType,
                                cardProductNameCode: cardProductNameCode ?? null,
                            })
                        }
                        return acc
                    }, []) ?? []

                state.partnership.companyCodes = payMethod.partnershipCardCodes ?? []
                // 카드 간편결제
                state.cardEasyPay.items = [
                    ...convertEasyCard(payMethod.cardEasyPays),
                    ...convertOverseaEasyCard(payMethod.overseasCardEasyPays),
                ]
                state.cardEasyPay.limitedInstallmentMonth = payMethod.billingPayMethods.limitedInstallmentMonth || null
                state.cardEasyPay.interestFreeInstallments = setupLongInterestFreeInstallments(
                    (payMethod.billingPayMethods.cardEasyPay?.interestFreeInstallments ?? []).map((info) => ({
                        ...info,
                        minAmount: getCommonInstallmentMinAmount(info.cardCompanyCode as PaymentsCardCodeType),
                    })) ?? [],
                    payMethod.billingPayMethods.cardEasyPay?.interestInstallments ?? [],
                )

                const firstItem = isEmpty(payMethod.overseasCardEasyPays)
                    ? getCardEasyPaysFirstItem<CardEasyPay>(({payServiceAvailable}) => payServiceAvailable)(
                          payMethod.cardEasyPays,
                      )
                    : getCardEasyPaysFirstItem<OverseaCardEasyPay>(
                          ({overseasCardStatusType}) => overseasCardStatusType === 'ACTIVE',
                      )(payMethod.overseasCardEasyPays)

                state.cardEasyPay.selected.id = firstItem?.cardId ?? ''
                state.cardEasyPay.selected.itemType = !firstItem ? EasyCardItemType.ADD_CARD : EasyCardItemType.CARD

                // 일반 카드결제
                state.creditCard.companies = payMethod.cardCompanies.map(({code, name}) => ({
                    name,
                    code: code as PaymentsCardCodeType,
                    baseInstallmentInfo: getCommonInstallments(code),
                }))

                state.creditCard.limitedInstallmentMonth = payMethod.billingPayMethods.limitedInstallmentMonth || null
                state.creditCard.interestFreeInstallments = setupLongInterestFreeInstallments(
                    payMethod.billingPayMethods.card?.interestFreeInstallments.map((info) => ({
                        ...info,
                        minAmount: getCommonInstallmentMinAmount(info.cardCompanyCode as PaymentsCardCodeType),
                    })) ?? [],
                    // @see https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2754#issuecomment-2145863
                    payMethod.billingPayMethods.cardEasyPay?.interestInstallments?.filter(
                        ({applicableCardProductCode}) => {
                            return isApplicableCardProductCodes(applicableCardProductCode ?? '')
                        },
                    ) ?? [],
                )

                state.creditCardBillDiscounts = payMethod.creditCardBillDiscounts.map((billDiscount) => ({
                    benefit: billDiscount.benefit,
                    cardCompanyCode: billDiscount.corporationCode as PaymentsCardCodeType,
                    discountRate: billDiscount.discountRate,
                    startDate: billDiscount.startDate,
                    endDate: billDiscount.endDate,
                    linkUrl: billDiscount.linkUrl,
                    additionDescription: billDiscount.orderAdditionDescription,
                    detailDescription: billDiscount.orderDetailDescription,
                    rank: billDiscount.rank,
                    eventType: billDiscount.eventType,
                }))
                state.partnership.containsCreditCard = payMethod.shinhanCardPartnership.containsCreditCard
                state.partnership.containsShinhanPartnershipCard =
                    payMethod.shinhanCardPartnership.containsShinhanPartnershipCard

                if (payMethod.billingPayMethods.cardEasyPay) {
                    state.cardEasyPay.billing = {
                        pgCp: payMethod.billingPayMethods.cardEasyPay.pgCp,
                        pgId: payMethod.billingPayMethods.cardEasyPay.pgId,
                        minimumAmount: payMethod.billingPayMethods.cardEasyPay.minimumAmount,
                        maximumAmount: payMethod.billingPayMethods.cardEasyPay.maximumAmount,
                    }
                }
                if (payMethod.billingPayMethods.card) {
                    state.creditCard.billing = {
                        pgCp: payMethod.billingPayMethods.card.pgCp,
                        pgId: payMethod.billingPayMethods.card.pgId,
                        sid: payMethod.billingPayMethods.card.sid,
                        minimumAmount: payMethod.billingPayMethods.card.minimumAmount,
                        maximumAmount: payMethod.billingPayMethods.card.maximumAmount,
                    }
                }

                if (payMethod.defaultPayment?.payMethodType === PayMethod.CARD) {
                    state.creditCard.selected.companyCode = payMethod.defaultPayment.cardCompanyCode
                        ? (payMethod.defaultPayment.cardCompanyCode as PaymentsCardCodeType)
                        : null
                }

                /**
                 * 포인트 프로모션
                 */
                // pointPromotion 객체가 없으면 프로모션 없음
                if (!pointPromotion) {
                    state.promotion.status = PromotionStatus.NO_PROMOTION
                } else {
                    const promotionStatus = getPointPromotionStatusByApiResult(pointPromotion.apiResultType)

                    /**
                     * PromotionStatus가 SUCCESS 이더라도 카드프로모션 카드사 목록이 비어있는 것이 보장이 안되는 케이스가 있음
                     * cardPromotionCompanyCodes 가 비어있으면 apiResultType과 관계없이 프로모션 없는 것으로 처리
                     * https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2635
                     */
                    state.promotion.status = isEmpty(pointPromotion.cardPromotionCompanyCodes)
                        ? PromotionStatus.NO_PROMOTION
                        : promotionStatus

                    // pointPromotion 상태가 unavailable이면 재조회 필요, apiStatus를 reject로 세팅
                    if (promotionStatus === PromotionStatus.UNAVAILABLE) {
                        state.promotion.apiStatus = APIStatus.REJECTED
                    } else if (promotionStatus === PromotionStatus.AVAILABLE) {
                        // pointPromotion 상태가 available이면 프로모션을 조회해올 수 있도록 code 목록 세팅
                        const generalCardPromotionCodes = pointPromotion.cardPromotionCompanyCodes

                        // 일반 프로모션 정책 조회 가능한 카드사 목록 먼저 초기화
                        state.promotion.groups = generalCardPromotionCodes.map((cardCompanyCode) => {
                            return getInitialPromotionInfo({cardCompanyCode, cardProductNameCode: undefined})
                        })

                        /**
                         * 카드상품 프로모션 정책 조회 가능한 카드 목록 초기화
                         * - 프로모션 정책 조회 이후 정보 갱신되기 때문에, cardCompanyCode는 일반 프로모션 코드값 셋팅
                         * */
                        const promotionCode = generalCardPromotionCodes[0]
                        if (promotionCode) {
                            state.promotion.groups = [
                                ...state.promotion.groups,
                                ...(pointPromotion.cardPromotionProductNameCodes
                                    .filter((cardProductNameCode) => isCardProductPromotion(cardProductNameCode))
                                    .map((cardProductNameCode) => {
                                        return getInitialPromotionInfo({
                                            cardCompanyCode: promotionCode,
                                            cardProductNameCode,
                                        })
                                    }) ?? []),
                            ]
                        }
                    }
                }
            })
            .addCase(getCardPromotionGroups.pending, (state) => {
                state.promotion.apiStatus = APIStatus.FETCHING
                state.promotion.groups = state.promotion.groups.map((group) => {
                    return getInitialPromotionInfo({
                        cardCompanyCode: group.card.companyCode,
                        cardProductNameCode: group.card.productNameCode,
                    })
                })
            })
            .addCase(getCardPromotionGroups.fulfilled, (state, {payload}) => {
                state.promotion.skipInfo = {
                    isSuggested: false,
                    isConnectedEasyBank: false,
                }

                const promotionStatus = getPointPromotionStatusByApiResult(payload.apiResultType)
                state.promotion.status = promotionStatus

                /**
                 * 조회된 프로모션 있는경우 > 유효한 프로모션은 업데이트, 아닌경우 정보 초기화
                 */
                if (promotionStatus === PromotionStatus.UNAVAILABLE) {
                    state.promotion.apiStatus = APIStatus.REJECTED
                } else if (promotionStatus === PromotionStatus.AVAILABLE) {
                    state.promotion.apiStatus = APIStatus.RESOLVED
                    const {generalPromotions, cardProductPromotions} = state.promotion.groups.reduce<{
                        generalPromotions: Array<GroupItem>
                        cardProductPromotions: Array<GroupItem>
                    }>(
                        (acc, next) => {
                            if (next.type === PromotionType.CARD_PRODUCT) {
                                acc.cardProductPromotions.push(next)
                            } else {
                                acc.generalPromotions.push(next)
                            }

                            return acc
                        },
                        {generalPromotions: [], cardProductPromotions: []},
                    )

                    /**
                     * 정책조회시 유효한 정책이 있는 프로모션 정보 저장
                     * - 우선순위에 따라 BE에서 정렬되어있으므로, 응답으로 받은 프로모션 정보들 먼저 업데이트
                     * - 나머지 제외된 프로모션 정보들은 초기화
                     **/

                    /** 조회된 프로모션 정보 우선 업데이트 */
                    const result = payload.groupedCardPromotions
                        .filter((group) => {
                            const {cardProductNameCode} = group

                            /** 일반 프로모션 이거나 카드상품 프로모션인데 FE에서 관리하고 있는 카드상품이어야한다. */
                            return !cardProductNameCode || isCardProductPromotion(cardProductNameCode)
                        })
                        .map((group) => {
                            const isGeneralPromotion = !group.cardProductNameCode
                            /**
                             * 업데이트 하려는 대상 프로모션이
                             * - 일반 프로모션인 경우 : companyCode가 key
                             * - 카드상품 프로모션인 경우 : productCode가 key
                             */
                            const validPromotionInfo = (
                                isGeneralPromotion ? generalPromotions : cardProductPromotions
                            ).find(({card: {companyCode, productNameCode}}) => {
                                return isGeneralPromotion
                                    ? companyCode === group.cardCompanyCode
                                    : productNameCode === group.cardProductNameCode
                            })

                            if (!validPromotionInfo) {
                                return getInitialPromotionInfo({
                                    cardCompanyCode: group.cardCompanyCode,
                                    cardProductNameCode: group.cardProductNameCode,
                                })
                            }

                            /**
                             * C1은 제휴 카드의 promotion policy에 담긴 productCode를 합집합하여 promotionCode로 사용한다
                             * see) https://oss.fin.navercorp.com/Product-Planning/Order_QA/issues/10857#issuecomment-1910796
                             */
                            const cardProductCodes = (() => {
                                /** cardProductNameCode가 없으면 일반 프로모션이므로 productCode가 무조건 null이다. */
                                if (!group.cardProductNameCode) {
                                    return undefined
                                }

                                return group.policies
                                    ? group.policies.reduce((acc, policy) => {
                                          if (policy.affiliateCard && policy.cardProductCodes) {
                                              const cardProductCodesAcc = [...acc, ...policy.cardProductCodes]

                                              return cardProductCodesAcc
                                          }

                                          return acc
                                      }, [] as string[])
                                    : undefined
                            })()

                            return {
                                card: {
                                    companyCode: group.cardCompanyCode,
                                    companyName: group.cardCompanyName,
                                    detailInfos: group.cardDetails
                                        ? group.cardDetails.map((detailInfo) => {
                                              return {
                                                  companyId: detailInfo.cardCompanyId,
                                                  issuerCode: detailInfo.cardIssuerCode,
                                                  issuerName: detailInfo.cardIssuerName,
                                                  types: detailInfo.cardTypes,
                                                  ownerTypes: detailInfo.cardOwnerTypes,
                                                  productCodes: cardProductCodes,
                                              }
                                          })
                                        : undefined,
                                    productNameCode: group.cardProductNameCode ?? undefined,
                                    productName: group.cardProductName,
                                },
                                totalPromotionApplyAmount: group.totalPromotionApplyAmount,
                                cardPromotions: group.policies
                                    ? group.policies.map((promotionInfo) => {
                                          return {
                                              policyNo: promotionInfo.promotionPolicyNo,
                                              accumulationPolicySequence: promotionInfo.accumulationPolicySequence,
                                              name: promotionInfo.promotionName,
                                              productNameCode: promotionInfo.cardProductNameCode ?? undefined,
                                              applyType: promotionInfo.promotionApplyType,
                                              applyValue: promotionInfo.promotionApplyValue,
                                              minPayAmount: promotionInfo.promotionMinPayAmount,
                                              maxApplyAmount: promotionInfo.promotionMaxApplyAmount,
                                              startDateTime: promotionInfo.startDateTime,
                                              endDateTime: promotionInfo.endDateTime,
                                              cautionText: promotionInfo.cautionText,
                                              isRestrictedByMembership: promotionInfo.restrictedByMembership,
                                              duplicateParticipation: promotionInfo.duplicateParticipation
                                                  ? {
                                                        restrictionType:
                                                            promotionInfo.duplicateParticipation.restrictionType,
                                                        maxOverlapCount:
                                                            promotionInfo.duplicateParticipation.restrictionCount,
                                                        applicableRemainCount:
                                                            promotionInfo.duplicateParticipation.applicableRemainCount,
                                                    }
                                                  : undefined,
                                              applicableProducts: promotionInfo.applicableOrderSheetItems
                                                  ? promotionInfo.applicableOrderSheetItems.map((productInfo) => {
                                                        return {
                                                            id: productInfo.orderSheetProductId,
                                                            itemId: productInfo.orderSheetItemId,
                                                            merchantId: productInfo.merchantProductId,
                                                            name: productInfo.productName,
                                                            applyAmount: productInfo.promotionApplyAmount,
                                                        }
                                                    })
                                                  : undefined,
                                          }
                                      })
                                    : [],
                                unApplicableReason: (function () {
                                    if (
                                        group.cardPromotions &&
                                        group.cardPromotions.every((promotion) => isEmpty(promotion.applicableProducts))
                                    ) {
                                        return PromotionUnApplicableReason.MINIMUM_PRODUCT_PRICE
                                    }
                                    return undefined
                                })(),
                                type: isGeneralPromotion ? PromotionType.GENERAL : PromotionType.CARD_PRODUCT,
                            }
                        })

                    /**
                     * 주문서 조회시의 모든 프로모션 조건 - 조회된 프로모션 조건 (차집합)
                     */
                    state.promotion.groups
                        .filter((group) => {
                            const {
                                card: {companyCode, productNameCode},
                            } = group

                            return !payload.groupedCardPromotions.some(({cardCompanyCode, cardProductNameCode}) =>
                                isEqual(
                                    {companyCode, productNameCode},
                                    {companyCode: cardCompanyCode, productNameCode: cardProductNameCode},
                                ),
                            )
                        })
                        /** 제외된 프로모션 정보 기본값으로 추가 */
                        .forEach(({card: {companyCode, productNameCode}}) => {
                            result.push(
                                getInitialPromotionInfo({
                                    cardCompanyCode: companyCode,
                                    cardProductNameCode: productNameCode,
                                }),
                            )
                        })

                    state.promotion.groups = result
                } else {
                    state.promotion.apiStatus = APIStatus.RESOLVED
                }
            })
            .addCase(getCardPromotionGroups.rejected, (state) => {
                state.promotion.status = PromotionStatus.UNAVAILABLE
                state.promotion.apiStatus = APIStatus.REJECTED
            })
    },
})

export default slice.reducer
